/*
 * Purpose: Pricing page subhead — A/B variants with experiment tracking
 * Last modified: 2026-06-29 | Agent
 * Completeness: 96
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import {
  assignPricingSubheadVariant,
  getPricingSubheadCopy,
  PRICING_SUBHEAD_EXPERIMENT_ID,
  type PricingSubheadVariant,
} from '@/lib/pricingSubheadExperiment';
import { trackExperimentEvent } from '@/lib/siteAnalytics';

export interface PricingSubheadAbProps {
  /** Ref to Stripe table wrapper — used for engagement conversion (table viewed). */
  pricingTableRef?: RefObject<HTMLElement | null>;
}

export function PricingSubheadAb({ pricingTableRef }: PricingSubheadAbProps) {
  const [variant] = useState<PricingSubheadVariant>(() => assignPricingSubheadVariant());
  const impressionSent = useRef(false);
  const tableViewSent = useRef(false);

  useEffect(() => {
    if (impressionSent.current) return;
    impressionSent.current = true;
    trackExperimentEvent(PRICING_SUBHEAD_EXPERIMENT_ID, variant, 'impression');
  }, [variant]);

  useEffect(() => {
    const target = pricingTableRef?.current;
    if (!target || tableViewSent.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting && e.intersectionRatio >= 0.35);
        if (!visible || tableViewSent.current) return;
        tableViewSent.current = true;
        trackExperimentEvent(PRICING_SUBHEAD_EXPERIMENT_ID, variant, 'pricing_table_view');
        observer.disconnect();
      },
      { threshold: [0.35, 0.5] }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [variant, pricingTableRef]);

  return (
    <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
      {getPricingSubheadCopy(variant)}
    </p>
  );
}
